import React, { useEffect, useMemo, useState } from "react";

// The paywall's close (X) flow — a Temu-style "free gift" on a spotlit stage:
//   gift   → "WAIT!" slams in, a gift box drops in and shakes, "Claim gift"
//   reveal → the box bursts (flash + confetti), a gold 25% OFF ticket flips in
//            with a count-up, and a live countdown
//   leave  → "Sure you want to leave?" with what they keep vs lose
// Every stage has its own entrance (no generic fades). The deadline comes from
// the server (services/exitOffer.js) and never resets.

export function formatLeft(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

// Confetti that bursts out from the middle (re-mounted on every stage change).
function Burst({ count = 46 }) {
  const pieces = useMemo(
    () => Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const dist = 140 + Math.random() * 220;
      return {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist - 60,
        r: Math.random() * 720 - 360,
        hue: [48, 330, 280, 190, 20, 140][i % 6],
        delay: Math.random() * 0.12,
        w: 6 + Math.random() * 6,
      };
    }),
    [count]
  );
  return (
    <div className="hpg-burst" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{ "--x": `${p.x}px`, "--y": `${p.y}px`, "--r": `${p.r}deg`, "--h": p.hue, "--w": `${p.w}px`, animationDelay: `${p.delay}s` }}
        />
      ))}
    </div>
  );
}

function Digit({ value }) {
  // Re-keyed on every change so each new number flips in.
  return <b key={value} className="hpg-digit">{value}</b>;
}

function Timer({ leftMs, label }) {
  const [h, m, s] = formatLeft(leftMs).split(":");
  return (
    <div className="hpg-timer" role="timer" aria-label={`${label} ${h}:${m}:${s}`}>
      <span className="hpg-timer-label">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5" /><path d="M9 2h6" /></svg>
        {label}
      </span>
      <span className="hpg-timer-row">
        <span className="hpg-tbox" style={{ "--d": "0.05s" }}><Digit value={h} /></span><i>:</i>
        <span className="hpg-tbox" style={{ "--d": "0.15s" }}><Digit value={m} /></span><i>:</i>
        <span className="hpg-tbox" style={{ "--d": "0.25s" }}><Digit value={s} /></span>
      </span>
    </div>
  );
}

// 0 → 25 count-up for the reveal.
function CountUp({ to, suffix }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / 700);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p >= 1) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [to]);
  return <>{n}{suffix}</>;
}

const Close = ({ onClick, label }) => (
  <button type="button" className="hpg-close" onClick={onClick} aria-label={label}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  </button>
);

export default function GiftOffer({ stage, t, leftMs, proPrice, lifePrice, busy, onClaim, onUse, onToLeave, onKeep, onLeave }) {
  // "Claim gift" plays the box-burst before the reveal shows.
  const [opening, setOpening] = useState(false);
  const claim = () => {
    if (opening || busy) return;
    setOpening(true);
    setTimeout(() => { onClaim(); setOpening(false); }, 650);
  };

  return (
    <div className="hpg" role="dialog" aria-modal="true">
      <div className="hpg-stagebg" aria-hidden="true">
        <span className="hpg-spot hpg-spot--l" />
        <span className="hpg-spot hpg-spot--r" />
        <span className="hpg-floor" />
        {Array.from({ length: 18 }).map((_, i) => <span key={i} className="hpg-star" style={{ "--i": i }} />)}
      </div>
      <div className="hpg-rain" aria-hidden="true">
        {Array.from({ length: 16 }).map((_, i) => <span key={i} style={{ "--i": i }} />)}
      </div>
      {stage !== "gift" && <Burst key={stage} />}
      {opening && <span className="hpg-flash" aria-hidden="true" />}

      {stage === "gift" && (
        <div className="hpg-stage" key="gift">
          <Close onClick={onToLeave} label={t("close")} />
          <p className="hpg-wait">{t("gift_kicker")}</p>
          <h2 className="hpg-title hpg-in-rise">{t("gift_title")}</h2>
          <div className={`hpg-gift${opening ? " is-opening" : ""}`} aria-hidden="true">
            <span className="hpg-rays" />
            <span className="hpg-halo" />
            <span className="hpg-box">🎁</span>
            <span className="hpg-spark hpg-spark--a">✦</span>
            <span className="hpg-spark hpg-spark--b">✦</span>
            <span className="hpg-spark hpg-spark--c">✦</span>
          </div>
          <button type="button" className="hpg-cta hpg-cta--pulse hpg-in-up" onClick={claim} disabled={busy || opening}>
            <span className="hpg-cta-shine" aria-hidden="true" />
            {t("gift_claim")}
          </button>
        </div>
      )}

      {stage === "reveal" && (
        <div className="hpg-stage" key="reveal">
          <Close onClick={onToLeave} label={t("close")} />
          <p className="hpg-unlocked hpg-in-drop"><span className="hpg-check">✓</span>{t("reveal_sub")}</p>
          <div className="hpg-ticket">
            <span className="hpg-ticket-shine" aria-hidden="true" />
            <span className="hpg-ticket-off hpg-metal"><CountUp to={25} suffix="%" /> OFF</span>
            <span className="hpg-ticket-line">{t("reveal_pro", { price: proPrice })}</span>
            <span className="hpg-ticket-line">{t("reveal_life", { price: lifePrice })}</span>
          </div>
          <Timer leftMs={leftMs} label={t("expires_in")} />
          <button type="button" className="hpg-cta hpg-in-up" onClick={onUse} style={{ animationDelay: "0.45s" }}>
            <span className="hpg-cta-shine" aria-hidden="true" />
            {t("reveal_cta")}
          </button>
        </div>
      )}

      {stage === "leave" && (
        <div className="hpg-stage" key="leave">
          <h2 className="hpg-title hpg-title--leave hpg-in-wobble">{t("leave_title")}</h2>
          <p className="hpg-sub hpg-in-rise" style={{ animationDelay: "0.1s" }}>{t("leave_sub")}</p>
          <div className="hpg-compare">
            <div className="hpg-card hpg-card--gold">
              <span className="hpg-card-label">{t("leave_yours")}</span>
              <span className="hpg-card-value hpg-metal--dark">{t("reveal_off")}</span>
            </div>
            <span className="hpg-arrow" aria-hidden="true">➜</span>
            <div className="hpg-card hpg-card--grey">
              <span className="hpg-card-emoji" aria-hidden="true">😢</span>
              <span className="hpg-card-label">{t("leave_without")}</span>
              <span className="hpg-card-value">0%</span>
            </div>
          </div>
          {leftMs > 0 && <Timer leftMs={leftMs} label={t("expires_in")} />}
          <button type="button" className="hpg-cta hpg-cta--pulse hpg-in-up" onClick={onKeep} style={{ animationDelay: "0.35s" }}>
            <span className="hpg-cta-shine" aria-hidden="true" />
            {t("leave_keep")}
          </button>
          <button type="button" className="hpg-still" onClick={onLeave}>{t("leave_still")}</button>
        </div>
      )}
    </div>
  );
}
